// TasksCalendarView — Calendar view for the Tasks page (Month/Week/Day),
// drag-and-drop rescheduling, unscheduled-tasks sidebar, and click-to-open
// task details. Pure presentational component — the parent (Tasks.js) owns
// task data, filters, and project scoping; this component only renders what
// it's given and reports back reschedule/quick-create/click intents.
//
// Parent contract:
//   <TasksCalendarView
//     tasks={filteredTasks}
//     priorityConfig={PRIORITY_CONFIG}
//     onTaskClick={(task) => void}
//     onReschedule={(task, newDateISOString) => Promise<void>}
//     onQuickCreate={(dateISOString) => void}
//   />

import { createContext, useContext, useMemo, useState } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  isSameMonth, isSameDay, isToday, format,
} from "date-fns";
import { ChevronLeft, ChevronRight, CheckCircle, Plus, Inbox } from "lucide-react";

// A task "occurs" on the first date it actually has — due date first, then
// any scheduled/start date fields, matching what the spec asks for and what
// exists on the data today (base Tasks only ever populate due_date; the
// Client Work Planner's generated tasks also carry start_date).
function taskDate(task) {
  const raw = task.due_date || task.scheduled_date || task.start_date;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function isCompleted(task) {
  return task.status === "completed" || task.status === "done";
}

// Service catalogue lookup (key → {label,color}) for chip colours.
const ServiceColorsContext = createContext({});

function TaskChip({ task, priorityConfig, onClick, draggable, onDragStart }) {
  const serviceByKey = useContext(ServiceColorsContext);
  const projectColor = serviceByKey[task.service]?.color || "#6366f1";
  const done = isCompleted(task);
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={(e) => { e.stopPropagation(); onClick(task); }}
      data-testid={`calendar-task-${task.task_id}`}
      className={`group flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] cursor-pointer border transition-all hover:shadow-sm ${
        done ? "bg-slate-50 border-slate-200 dark:bg-slate-800/50 dark:border-slate-700" : "bg-white border-slate-200 dark:bg-slate-800 dark:border-slate-700"
      }`}
      style={{ borderLeftColor: projectColor, borderLeftWidth: 3 }}
      title={task.title}
    >
      {done ? (
        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
      ) : (
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${priorityConfig[task.priority]?.dotColor || "bg-slate-400"}`} />
      )}
      {/* Work-planner-generated activities carry source: 'work_planner';
          plain tasks never have this field, so the badge simply never
          renders on the regular Tasks page. */}
      {task.source === "work_planner" && <span className="flex-shrink-0" title="Planned Activity">📅</span>}
      <span className={`truncate ${done ? "line-through text-slate-400" : "text-slate-700 dark:text-slate-200"}`}>
        {task.title}
      </span>
    </div>
  );
}

function DayCell({ day, inMonth, tasks, priorityConfig, onTaskClick, onDropTask, onQuickCreate, dragOver, setDragOver }) {
  const key = format(day, "yyyy-MM-dd");
  const visible = tasks.slice(0, 3);
  const overflow = tasks.length - visible.length;

  return (
    <div
      data-testid={`calendar-day-${key}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
      onDragLeave={() => setDragOver((cur) => (cur === key ? null : cur))}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(null);
        const taskId = e.dataTransfer.getData("text/task_id");
        if (taskId) onDropTask(day, taskId);
      }}
      onClick={() => onQuickCreate(day)}
      className={`min-h-[92px] border border-slate-100 dark:border-slate-800 p-1.5 flex flex-col gap-1 cursor-pointer transition-colors ${
        inMonth ? "bg-white dark:bg-slate-900" : "bg-slate-50/60 dark:bg-slate-900/40"
      } ${dragOver === key ? "ring-2 ring-inset ring-indigo-400 bg-indigo-50/60 dark:bg-indigo-900/20" : ""} hover:bg-slate-50 dark:hover:bg-slate-800/40`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium ${
          isToday(day) ? "w-5 h-5 flex items-center justify-center rounded-full bg-indigo-600 text-white" :
          inMonth ? "text-slate-600 dark:text-slate-300" : "text-slate-300 dark:text-slate-600"
        }`}>
          {format(day, "d")}
        </span>
        {tasks.length > 0 && <span className="text-[10px] text-slate-400">{tasks.length}</span>}
      </div>
      <div className="flex-1 space-y-1 overflow-hidden">
        {visible.map((t) => (
          <TaskChip
            key={t.task_id}
            task={t}
            priorityConfig={priorityConfig}
            onClick={onTaskClick}
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/task_id", t.task_id)}
          />
        ))}
        {overflow > 0 && <div className="text-[10px] text-slate-400 pl-1">+{overflow} more</div>}
      </div>
    </div>
  );
}

export default function TasksCalendarView({ tasks, priorityConfig, onTaskClick, onReschedule, onQuickCreate, initialDate, serviceByKey = {} }) {
  const [calMode, setCalMode] = useState("month"); // month | week | day
  const [anchor, setAnchor] = useState(initialDate || new Date());
  const [dragOver, setDragOver] = useState(null);

  const byDateKey = useMemo(() => {
    const map = {};
    for (const t of tasks) {
      const d = taskDate(t);
      if (!d) continue;
      const key = format(d, "yyyy-MM-dd");
      (map[key] = map[key] || []).push(t);
    }
    return map;
  }, [tasks]);

  const unscheduled = useMemo(() => tasks.filter((t) => !taskDate(t)), [tasks]);

  const rangeDays = useMemo(() => {
    if (calMode === "day") return [anchor];
    if (calMode === "week") {
      return eachDayOfInterval({ start: startOfWeek(anchor, { weekStartsOn: 1 }), end: endOfWeek(anchor, { weekStartsOn: 1 }) });
    }
    const gridStart = startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [calMode, anchor]);

  const title = calMode === "day"
    ? format(anchor, "d MMMM yyyy")
    : calMode === "week"
      ? `${format(startOfWeek(anchor, { weekStartsOn: 1 }), "d MMM")} – ${format(endOfWeek(anchor, { weekStartsOn: 1 }), "d MMM yyyy")}`
      : format(anchor, "MMMM yyyy");

  const goPrev = () => setAnchor((d) => (calMode === "day" ? subDays(d, 1) : calMode === "week" ? subWeeks(d, 1) : subMonths(d, 1)));
  const goNext = () => setAnchor((d) => (calMode === "day" ? addDays(d, 1) : calMode === "week" ? addWeeks(d, 1) : addMonths(d, 1)));

  const dropOnDay = async (day, taskId) => {
    const task = tasks.find((t) => t.task_id === taskId);
    if (!task) return;
    const existing = taskDate(task);
    const newDate = new Date(day);
    if (existing) newDate.setHours(existing.getHours(), existing.getMinutes());
    await onReschedule(task, newDate.toISOString());
  };

  const weekdayLabels = eachDayOfInterval({ start: startOfWeek(new Date(), { weekStartsOn: 1 }), end: endOfWeek(new Date(), { weekStartsOn: 1 }) })
    .map((d) => format(d, "EEE"));

  return (
    <ServiceColorsContext.Provider value={serviceByKey}>
    <div className="space-y-3" data-testid="tasks-calendar-view">
      {/* Header: nav + today + view selector */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-slate-800 rounded-xl p-3 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <button onClick={goPrev} data-testid="calendar-prev" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={goNext} data-testid="calendar-next" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ChevronRight className="w-4 h-4" />
          </button>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white min-w-[140px]">{title}</h3>
          <button onClick={() => setAnchor(new Date())} data-testid="calendar-today" className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700">
            Today
          </button>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
          {["month", "week", "day"].map((m) => (
            <button
              key={m}
              onClick={() => setCalMode(m)}
              data-testid={`calendar-mode-${m}`}
              className={`px-3 py-1 text-xs font-medium rounded-md capitalize transition-colors ${
                calMode === m ? "bg-white dark:bg-slate-900 shadow-sm text-indigo-600" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Calendar grid — collapses to a simple date-grouped agenda below md */}
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {calMode !== "day" && (
            <div className="hidden md:grid grid-cols-7 border-b border-slate-100 dark:border-slate-800">
              {weekdayLabels.map((w) => (
                <div key={w} className="text-center text-[11px] font-semibold text-slate-400 py-2">{w}</div>
              ))}
            </div>
          )}

          {/* Desktop/tablet grid */}
          <div className={`hidden md:grid ${calMode === "day" ? "grid-cols-1" : "grid-cols-7"}`}>
            {rangeDays.map((day) => (
              <DayCell
                key={day.toISOString()}
                day={day}
                inMonth={calMode !== "month" || isSameMonth(day, anchor)}
                tasks={byDateKey[format(day, "yyyy-MM-dd")] || []}
                priorityConfig={priorityConfig}
                onTaskClick={onTaskClick}
                onDropTask={dropOnDay}
                onQuickCreate={onQuickCreate}
                dragOver={dragOver}
                setDragOver={setDragOver}
              />
            ))}
          </div>

          {/* Mobile agenda — a plain date-grouped list, touch-friendly, no grid/DnD */}
          <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
            {rangeDays.map((day) => {
              const dayTasks = byDateKey[format(day, "yyyy-MM-dd")] || [];
              if (calMode === "month" && dayTasks.length === 0) return null;
              return (
                <div key={day.toISOString()} className="p-3" data-testid={`agenda-day-${format(day, "yyyy-MM-dd")}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-semibold ${isToday(day) ? "text-indigo-600" : "text-slate-700 dark:text-slate-200"}`}>
                      {format(day, "EEE, d MMM")}
                    </span>
                    <button onClick={() => onQuickCreate(day)} className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700">
                      <Plus className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  </div>
                  {dayTasks.length === 0 ? (
                    <p className="text-xs text-slate-400">No tasks</p>
                  ) : (
                    <div className="space-y-1.5">
                      {dayTasks.map((t) => (
                        <TaskChip key={t.task_id} task={t} priorityConfig={priorityConfig} onClick={onTaskClick} draggable={false} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Unscheduled tasks sidebar — drag onto a calendar day to schedule */}
        {unscheduled.length > 0 && (
          <div className="w-full lg:w-64 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3" data-testid="calendar-unscheduled-sidebar">
            <div className="flex items-center gap-2 mb-3 text-slate-600 dark:text-slate-300">
              <Inbox className="w-4 h-4" />
              <h4 className="text-xs font-semibold uppercase tracking-wide">Unscheduled ({unscheduled.length})</h4>
            </div>
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
              {unscheduled.map((t) => (
                <TaskChip
                  key={t.task_id}
                  task={t}
                  priorityConfig={priorityConfig}
                  onClick={onTaskClick}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/task_id", t.task_id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </ServiceColorsContext.Provider>
  );
}
